import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useConversations } from '@/hooks/use-conversations-v2';
import { conversationToDeal } from '@/types';
import { Skeleton } from '@/components/ui';
import { PipelineSummary, DealTable, DealMap } from '@/components/pipeline';
import {
  type PipelineStatus,
  pipelineStatusSchema,
} from '@arcanum-hq/shared-types';

// ── validateSearch schema ─────────────────────────────────────────────────────
// C2: collapsed from multi-select array to single optional status param.
// Degrade-not-throw precedent (builderSearchSchema / adminSearchSchema):
// unknown status value → undefined (= show all), never throws.
export const pipelineSearchSchema = z.object({
  status: pipelineStatusSchema.optional().catch(undefined),
});

export type PipelineSearch = z.infer<typeof pipelineSearchSchema>;

// ── PipelinePage ──────────────────────────────────────────────────────────────
export function PipelinePage() {
  const { data: conversationsData, isLoading, error } = useConversations();
  const search = useSearch({ from: '/pipeline' });
  const navigate = useNavigate({ from: '/pipeline' });

  // ADR 0018 client-side gate: only show conversations WITH ≥1 version.
  // Keep the existing [Solver] exclusion verbatim.
  const versionGatedDeals = useMemo(() => {
    const convs = conversationsData?.data ?? [];
    return convs
      .filter((c) => !c.title.startsWith('[Solver]'))
      .filter((c) => c.hasVersion === true)
      .map(conversationToDeal);
  }, [conversationsData]);

  // C2: single URL status param — lock/toggle via click, preview via hover.
  // statusFilter = locked URL value; hoverStatus = transient preview.
  // activeStatus = statusFilter ?? hoverStatus (Lovable single-status model).
  const statusFilter: PipelineStatus | undefined = search?.status;
  const [hoverStatus, setHoverStatus] = useState<PipelineStatus | null>(null);
  const activeStatus: PipelineStatus | null =
    statusFilter ?? hoverStatus ?? null;

  // C2: click locks/toggles the single URL `status` param.
  // Functional navigate form — never replace-form for in-place URL transitions.
  const handleStatusClick = useCallback(
    (s: string | null) => {
      const next = s && s !== statusFilter ? (s as PipelineStatus) : undefined;
      void navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          status: next,
        }),
        replace: true,
      });
    },
    [statusFilter, navigate]
  );

  // C2: hover previews only when nothing is locked (statusFilter absent).
  const handleStatusHover = useCallback(
    (s: string | null) => {
      if (!statusFilter) setHoverStatus(s as PipelineStatus | null);
    },
    [statusFilter]
  );

  // C1/C2: Summary denominator = full versionGatedDeals (cap-bar % + BY STATUS
  // counts reflect the WHOLE pipeline). Table + map receive full array; dimming
  // is handled inside DealTable via opacity:0.25 on non-matching rows.
  // filteredDeals feeds only the DealMap (active pins).
  const filteredDeals = useMemo(() => {
    if (!activeStatus) return versionGatedDeals;
    return versionGatedDeals.filter((d) => d.status === activeStatus);
  }, [versionGatedDeals, activeStatus]);

  const [hoverDeal, setHoverDeal] = useState(-1);
  const [selectedDeal, setSelectedDeal] = useState(-1);

  const activeDeal = selectedDeal >= 0 ? selectedDeal : hoverDeal;

  const selectDeal = useCallback((i: number) => {
    setSelectedDeal((prev) => (prev === i ? -1 : i));
  }, []);

  const handleHoverDeal = useCallback(
    (i: number) => {
      if (selectedDeal < 0) setHoverDeal(i);
    },
    [selectedDeal]
  );

  const handleLeaveDeal = useCallback(() => {
    if (selectedDeal < 0) setHoverDeal(-1);
  }, [selectedDeal]);

  // Loading — keep skeleton width aligned to new 298px Summary card (C3)
  if (isLoading) {
    return (
      <div className="flex flex-col h-full p-3 gap-2.5 overflow-hidden">
        <div className="flex gap-2.5 shrink-0 max-h-[42%] min-h-0">
          <div className="w-[298px] min-w-[298px] bg-panel-bg border border-panel-border rounded-[3px] p-3">
            <Skeleton className="h-4 w-20 mb-3" />
            <Skeleton variant="block" className="h-16 mb-2" />
            <Skeleton className="h-3 w-full mb-1" />
            <Skeleton className="h-3 w-full mb-1" />
            <Skeleton className="h-3 w-full" />
          </div>
          <div className="flex-1 bg-panel-bg border border-panel-border rounded-[3px] p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full mb-2" />
            ))}
          </div>
        </div>
        <div className="flex-1 bg-panel-bg border border-panel-border rounded-[3px] p-3">
          <Skeleton variant="block" className="h-full" />
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="flex flex-col h-full p-3 gap-2.5 overflow-hidden items-center justify-center">
        <span className="text-sm font-mono font-semibold text-text-muted tracking-[1px]">
          FAILED TO LOAD PIPELINE
        </span>
        <span className="text-sm font-body text-text-dim">{error.message}</span>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-3 py-1 bg-accent-dim border border-accent/15 rounded-[2px] text-accent text-sm font-mono font-semibold cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  // C1: ALWAYS render the 3-zone layout — no early-return collapse for
  // versionGatedDeals.length === 0 or filteredDeals.length === 0.
  // Empty → DealTable renders inline caption; DealMap renders US path + zero pins.
  return (
    <div className="flex flex-col h-full p-3 gap-2.5 overflow-hidden">
      {/* Top row: Summary card (full versionGatedDeals) + Deal Pipeline table */}
      <div className="flex gap-2.5 shrink-0 max-h-[42%] min-h-0">
        {/* C1/C2: Summary receives full versionGatedDeals — denominator is
            the whole pipeline. activeStatus drives the BY-STATUS highlight. */}
        <PipelineSummary
          deals={versionGatedDeals}
          activeStatus={activeStatus}
          onStatusClick={handleStatusClick}
          onStatusHover={handleStatusHover}
          statusFilter={statusFilter ?? null}
        />
        {/* C1/C2: DealTable receives full versionGatedDeals — non-matching rows
            dim to 0.25 opacity rather than being removed. activeStatus passed
            down so the row-level opacity:0.25 logic activates correctly. */}
        <DealTable
          deals={versionGatedDeals}
          activeDeal={activeDeal}
          selectedDeal={selectedDeal}
          activeStatus={activeStatus}
          onSelectDeal={selectDeal}
          onHoverDeal={handleHoverDeal}
          onLeaveDeal={handleLeaveDeal}
        />
      </div>

      {/* Bottom row: full-bleed map (C6: DealSignal removed).
          MUST be `flex flex-col` — <DealMap>'s root uses `flex-1 … min-h-0`
          which only clamps to this row's height when the parent is a flex
          container. C6 removed the sibling <DealSignal>; the row lost its
          `flex` and the map SVG blew out to its intrinsic ~750px, overflowing
          the page (the "map still cut off" regression, 2026-05-18). */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* C2/C1: map receives filteredDeals (active pins) — pins for non-matching
            statuses are simply absent from the map when a filter is locked. */}
        <DealMap
          deals={filteredDeals}
          activeDeal={activeDeal}
          onDotClick={selectDeal}
          onDotHover={handleHoverDeal}
          onDotLeave={handleLeaveDeal}
        />
      </div>
    </div>
  );
}
